<template>
  <div class="login-container d-flex flex-column align-center justify-center text-center px-4">
    <app-logo class="mb-6" />

    <h1 class="text-h6 font-weight-medium mb-2">
      {{ t("authentication.signInTitle") }}
    </h1>

    <p class="text-body-2 text-medium-emphasis mb-6">
      {{ t("authentication.signInSubtitle") }}
    </p>

    <v-progress-circular v-if="isSigningIn" indeterminate color="primary" />

    <v-btn v-else color="primary" size="large" @click="signIn()">
      {{ t("authentication.signInAction") }}
    </v-btn>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, onUnmounted, ref } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";

import AppLogo from "@/components/AppLogo.vue";
import { initiateAuthorize } from "@/api/idp";
import { normalizeRedirect, shouldAutoSignIn } from "@/router/authGuards";

const { t } = useI18n();
const route = useRoute();

const isSigningIn = ref(false);

const onPageShow = (event: PageTransitionEvent) => {
  if (event.persisted) {
    isSigningIn.value = false;
  }
};

const signIn = async () => {
  try {
    isSigningIn.value = true;
    const redirect =
      normalizeRedirect(typeof route.query.redirect === 'string' ? route.query.redirect : null) ||
      normalizeRedirect(sessionStorage.getItem('dx_internal_redirect')) ||
      '/';
    await initiateAuthorize(redirect);
  } catch (error) {
    isSigningIn.value = false;
    console.error('Failed to initiate login:', error);
  }
};

onMounted(() => {
  window.addEventListener("pageshow", onPageShow);
  if (shouldAutoSignIn(route.query.auto)) {
    signIn();
  }
});

onUnmounted(() => {
  window.removeEventListener("pageshow", onPageShow);
});
</script>

<style scoped>
.login-container {
  min-height: 80vh;
}
</style>
